<script lang="ts" setup>
import { type VNodeChild } from 'vue';
import { IconCircleArrowUpFilled, IconCircleArrowDownFilled } from '@/icons';
import { hasSlotContent } from '../../utils';

interface Props {
  size?: 's' | 'm';
  diffType?: 'positive' | 'negative';
}

const { diffType = 'positive', size = 'm' } = defineProps<Props>();

defineSlots<{
  header?: () => VNodeChild;
  default?: () => VNodeChild;
  meta?: () => VNodeChild;
  diff?: () => VNodeChild;
  diffSuffix?: () => VNodeChild;
}>();
</script>

<template>
  <div :class="[$style.root, { [$style.sizeS]: size === 's', [$style.sizeM]: size === 'm' }]">
    <div :class="$style.main">
      <div v-if="hasSlotContent($slots.header)" :class="$style.header">
        <slot name="header" />
      </div>
      <div v-if="hasSlotContent($slots.default)" :class="$style.text">
        <slot />
      </div>
    </div>
    <div v-if="hasSlotContent($slots.meta) || hasSlotContent($slots.diff)" :class="$style.bottom">
      <div v-if="hasSlotContent($slots.meta)" :class="$style.meta">
        <slot name="meta" />
      </div>
      <div
        v-if="hasSlotContent($slots.diff)"
        :class="[
          $style.diff,
          {
            [$style.diffPositive]: diffType === 'positive',
            [$style.diffNegative]: diffType === 'negative',
          },
        ]"
      >
        <div :class="$style.diffIcon">
          <IconCircleArrowUpFilled v-if="diffType === 'positive'" />
          <IconCircleArrowDownFilled v-else-if="diffType === 'negative'" />
        </div>
        <div>
          <div :class="$style.diffValue">
            <slot name="diff" />
          </div>
        </div>
        <div v-if="hasSlotContent($slots.diffSuffix)" :class="$style.diffSuffix">
          <slot name="diffSuffix" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  color: #010000;
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  gap: 4px;
  min-height: 80px;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sizeS .main {
  gap: 4px;
}

.header {
  font-size: 13px;
  line-height: 16px;
  font-weight: 500;
}

.text {
  font-weight: 700;
  font-size: 22px;
  line-height: 24px;
}

.sizeS .text {
  font-size: 16px;
  line-height: 20px;
}

.meta {
  color: #978f91;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
}

.bottom {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.diff {
  display: flex;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
}

.diffIcon {
  width: 16px;
  height: 16px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.diffValue {
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}

.diffSuffix {
  color: #978f91;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.diffPositive {
  & .diffValue,
  & .diffIcon {
    color: #00906e;
  }
}

.diffNegative {
  & .diffValue,
  & .diffIcon {
    color: #e63946;
  }
}
</style>
